import React, { createContext, useContext, useState, ReactNode } from 'react';

// Brand Colors
export const brandColors = {
  orange: '#FFB34F',      // R: 255; G: 179; B: 79
  green: '#00993F',        // R: 0; G: 153; B: 63
  gray: '#BEC3CE',         // R: 190; G: 195; B: 206
  blue: '#3F84D2',         // R: 63; G: 132; B: 210
  darkBlue: '#00416E',     // R: 0; G: 65; B: 110
  red: '#F05E5E',          // R: 240; G: 94; B: 94
  black: '#000000',        // R: 0; G: 0; B: 0
  white: '#FFFFFF',
  lightGray: '#F5F5F5',
  darkGray: '#333333'
};

// Dark mode colors
export const darkColors = {
  orange: '#FFB34F',      // Keep orange the same
  green: '#10B981',       // Brighter green for dark mode
  gray: '#6B7280',        // Medium gray for dark mode
  blue: '#60A5FA',        // Brighter blue for dark mode
  darkBlue: '#93C5FD',    // Light blue for headings in dark mode
  red: '#F87171',         // Brighter red for dark mode
  black: '#000000',
  white: '#FFFFFF',
  lightGray: '#111827',   // Very dark background (almost black)
  darkGray: '#D1D5DB'     // Light gray text for dark mode
};

export const lightTheme = {
  colors: {
    ...brandColors,
    cardBackground: '#FFFFFF',
    containerBackground: '#F5F5F5'
  },
  fonts: {
    primary: '"Inter", "Segoe UI", "Roboto", sans-serif',
    secondary: '"Georgia", serif'
  },
  breakpoints: {
    mobile: '768px',
    tablet: '1024px',
    desktop: '1200px'
  },
  spacing: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem',
    xxl: '4rem'
  },
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px'
  },
  shadows: {
    sm: '0 2px 4px rgba(0, 0, 0, 0.1)',
    md: '0 4px 8px rgba(0, 0, 0, 0.1)',
    lg: '0 8px 16px rgba(0, 0, 0, 0.1)',
    xl: '0 16px 32px rgba(0, 0, 0, 0.1)'
  }
};

export const darkTheme = {
  ...lightTheme,
  colors: {
    ...darkColors,
    cardBackground: '#1F2937',    // Darker card background
    containerBackground: '#111827' // Very dark container background
  },
  shadows: {
    sm: '0 2px 4px rgba(0, 0, 0, 0.5)',
    md: '0 4px 8px rgba(0, 0, 0, 0.5)',
    lg: '0 8px 16px rgba(0, 0, 0, 0.5)',
    xl: '0 16px 32px rgba(0, 0, 0, 0.5)'
  }
};

interface ThemeContextType {
  theme: typeof lightTheme;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  const value = {
    theme: isDarkMode ? darkTheme : lightTheme,
    isDarkMode,
    toggleDarkMode
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
